import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import { getProductsMatchingSearch } from 'common/modules/products/utils';

import ProductsTable from 'components/ProductsTable';
import SearchBar from 'components/SearchBar';
import SearchBarMenu from 'components/SearchBar/SearchBarMenu';
import LinkButton from 'common/components/LinkButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import EmptyState from 'common/components/states/EmptyState';

const Products = () => {
	const { t } = useTranslation();

	const allProducts = useSelector(StockSelectors.stockProductsData);
	const activeLocationProducts = useSelector(StockSelectors.activeLocationProducts);
	const categoriesById = useSelector(StockSelectors.categoriesById);
	const activeLocation = useSelector(UserSelectors.userActiveLocation);
	const lang = useSelector(LanguageSelectors.language);
	const shopLocationCount = useSelector(ShopSelectors.allShopLocationsCount);
	const [searchValue, setSearchValue] = useState<string>('');
	const [storeFilter, setStoreFilter] = useState<string>('current-store');

	const hasMultipleLocations = shopLocationCount > 1;
	const hasProducts = allProducts.length > 0;

	const productsToShow =
		!hasMultipleLocations || storeFilter === 'all-stores' ? allProducts : activeLocationProducts;

	const filteredProducts = getProductsMatchingSearch(searchValue, {
		products: productsToShow,
		categoriesById,
		lang,
	});

	const stockProductsLoading = useSelector(StockSelectors.stockProductsLoading);

	const newProductButton = (
		<LinkButton to={Routes.CATALOG_CREATE_PRODUCT} variant="contained" color="primary">
			{t('products.newProduct', 'New product')}
		</LinkButton>
	);

	return (
		<>
			<SetPageTitle
				title={t('products.catalog', 'Catalog')}
				priority={2}
				styling={{ fluid: true }}
				rightContent={hasProducts ? newProductButton : undefined}
			/>
			{hasProducts ? (
				<Box>
					<Box flex={1}>
						<SearchBar
							onChange={setSearchValue}
							placeholder={t('rentals.search.searchForProduct', 'Search for a product')}
							endAdornment={
								hasMultipleLocations ? (
									<SearchBarMenu
										value={storeFilter}
										onChange={setStoreFilter}
										options={[
											{
												key: 'current-store',
												label: activeLocation?.name ?? '',
											},
											{
												key: 'all-stores',
												label: t('common:shopLocation.allStores'),
											},
										]}
									/>
								) : null
							}
						/>
					</Box>
					<ProductsTable
						products={filteredProducts}
						searchValue={searchValue}
						loading={stockProductsLoading}
					/>
				</Box>
			) : (
				<EmptyState
					cta={newProductButton}
					title={t('ProductsTable.noProducts', 'You have no products')}
					description={t('ProductsTable.noProductsDescriptionNew')}
				></EmptyState>
			)}
		</>
	);
};

export default Products;
